import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from "../../components/header";

const AboutUs = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.3, 0.1, 0.3, 1]
      }
    }
  };

  const statVariants = {
    hidden: { scale: 0.8, opacity: 0, rotate: -5 },
    visible: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        duration: 0.7,
        ease: "backOut",
        type: "spring",
        stiffness: 100
      }
    },
    hover: {
      scale: 1.05,
      y: -10,
      boxShadow: "0 15px 40px rgba(92, 182, 249, 0.2)",
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const highlightVariants = {
    hidden: { width: 0 },
    visible: {
      width: "100%",
      transition: {
        duration: 0.8,
        ease: [0.3, 0.1, 0.3, 1],
        delay: 0.5
      }
    }
  };

  const dividerVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        duration: 0.8,
        ease: "anticipate",
        delay: 0.3
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [-10, 10],
      transition: {
        y: {
          duration: 3,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }
      }
    }
  };

  return (
    <div className="about-us-container">
      <Header />
      <br/>
      <motion.section 
        className="about-us-section"
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        {/* Floating background elements */}
        <motion.div 
          className="floating-circle big"
          variants={floatingVariants}
          animate="float"
          style={{
            x: -100,
            y: 50,
            transition: { ...floatingVariants.float.transition, delay: 0.5 }
          }}
        />
        <motion.div 
          className="floating-circle medium"
          variants={floatingVariants}
          animate="float"
          style={{
            x: 200,
            y: -30,
            transition: { ...floatingVariants.float.transition, delay: 1 }
          }}
        />
        <motion.div 
          className="floating-circle small"
          variants={floatingVariants}
          animate="float"
          style={{
            x: -150,
            y: 150,
            transition: { ...floatingVariants.float.transition, delay: 1.5 }
          }}
        />

        <div className="about-us-content">
          <motion.div className="about-us-text" variants={itemVariants}>
            <motion.div variants={itemVariants}>
              <motion.p 
                className="section-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                WHO WE ARE
              </motion.p>
              <h1>
                About{" "}
                <span className="highlight">
                  Mabunda Tech
                  <motion.span 
                    className="highlight-underline"
                    variants={highlightVariants}
                    initial="hidden"
                    animate={controls}
                  />
                </span>
              </h1>
              <motion.div 
                className="divider"
                variants={dividerVariants}
              />
            </motion.div>
            
            <motion.p variants={itemVariants}>
              Founded in 2025, Mabunda Tech was born from a vision to empower businesses and individuals through innovative technology solutions. We bridge the gap between cutting-edge technology and practical business needs.
            </motion.p>
            
            <motion.p variants={itemVariants}>
              As a dynamic startup, we combine passion with expertise to deliver solutions that not only meet but exceed expectations. Our team of dedicated professionals is committed to driving digital transformation that creates real impact.
            </motion.p>
          </motion.div>

          <motion.div 
            className="about-us-stats" 
            variants={containerVariants}
            initial="hidden"
            animate={controls}
          >
            <motion.div 
              className="stat"
              variants={statVariants}
              whileHover="hover"
            >
              <h2>2025</h2>
              <p>Year Founded</p>
              <div className="stat-decoration"></div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      <style jsx>{`
        .about-us-container {
          width: 100%;
          overflow: hidden;
          position: relative;
        }
        
        .about-us-section {
          padding: 100px 20px;
          background-color: white;
          color: #333;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          overflow: hidden;
        }
        
        .floating-circle {
          position: absolute;
          border-radius: 50%;
          filter: blur(40px);
          opacity: 0.3;
          z-index: 0;
        }
        
        .floating-circle.big {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, #5cb6f9 0%, transparent 70%);
        }
        
        .floating-circle.medium {
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, #02f7ee 0%, transparent 70%);
        }
        
        .floating-circle.small {
          width: 150px;
          height: 150px;
          background: radial-gradient(circle, #7b2ff7 0%, transparent 70%);
        }

        .about-us-content {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .about-us-text {
          margin-bottom: 60px;
          font-size: 1.1rem;
          color: #555;
        }

        .about-us-text h1 {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #222;
          line-height: 1.2;
          position: relative;
        }
        
        .highlight {
          color: #5cb6f9;
          position: relative;
          display: inline-block;
        }
        
        .highlight-underline {
          position: absolute;
          bottom: 5px;
          left: 0;
          width: 100%;
          height: 8px;
          background: rgba(2, 247, 238, 0.3);
          z-index: -1;
          transform-origin: left;
        }

        .section-subtitle {
          font-size: 1rem;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #5cb6f9;
          margin-bottom: 15px;
          font-weight: 600;
        }
        
        .divider {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, #5cb6f9, #02f7ee);
          margin: 20px auto 30px;
          border-radius: 3px;
          transform-origin: left;
        }

        .about-us-text p {
          font-size: 1.2rem;
          margin: 0 auto 25px;
          max-width: 800px;
          line-height: 1.8;
          color: #555;
        }

        .about-us-stats {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: 40px;
          margin-top: 60px;
        }

        .stat {
          text-align: center;
          padding: 30px 40px;
          position: relative;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          min-width: 180px;
          border: 1px solid rgba(0, 0, 0, 0.03);
          cursor: default;
          z-index: 1;
          transform-style: preserve-3d;
        }
        
        .stat::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(135deg, #5cb6f9, #02f7ee);
          border-radius: 14px;
          z-index: -1;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .stat:hover::before {
          opacity: 0.3;
        }
        
        .stat-decoration {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #5cb6f9, #02f7ee);
          border-radius: 12px 12px 0 0;
        }

        .stat h2 {
          font-size: 2.5rem;
          margin-bottom: 10px;
          color: #222;
          font-weight: 700;
          background: linear-gradient(135deg, #5cb6f9, #02f7ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .stat p {
          font-size: 1rem;
          color: #777;
          font-weight: 500;
          letter-spacing: 1px;
        }

        @media (max-width: 768px) {
          .about-us-section {
            padding: 80px 20px;
          }
          
          .about-us-text h1 {
            font-size: 2.2rem;
          }

          .about-us-text p {
            font-size: 1rem;
          }
          
          .stat {
            padding: 20px;
            min-width: 120px;
          }
          
          .stat h2 {
            font-size: 2rem;
          }
          
          .floating-circle {
            filter: blur(30px);
          }
          
          .floating-circle.big {
            width: 200px;
            height: 200px;
          }
          
          .floating-circle.medium {
            width: 150px;
            height: 150px;
          }
          
          .floating-circle.small {
            width: 100px;
            height: 100px;
          }
        }

        @media (max-width: 480px) {
          .stat {
            width: 100%;
            max-width: 220px;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutUs;